import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import RichTextEditor, { markdownToHtml } from '../../components/RichTextEditor';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import api from '../../services/api';
import { toast } from 'sonner';
import { 
  FileText, 
  Save,
  Send,
  History,
  Eye,
  RotateCcw,
  Clock,
  User,
  CheckCircle,
  AlertCircle,
  Shield,
  Truck,
  Scale,
  RefreshCw,
  Ban,
  Users,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { sanitizeHtml } from '../../utils/sanitize';

// ============ Constants ============
const DOCUMENT_ICONS = {
  privacy_policy: Shield,
  shipping_policy: Truck,
  terms_of_service: Scale,
  return_refund_policy: RefreshCw,
  do_not_sell_info: Ban
};

const ACKNOWLEDGEMENT_TYPES = [
  { value: 'none', label: 'No Acknowledgement Required', description: 'Document is informational only' },
  { value: 'soft', label: 'Soft Acknowledgement', description: 'User clicks "I acknowledge" - name/email captured' },
  { value: 'hard', label: 'Hard Acknowledgement (Signature)', description: 'User must type exact name and email - blocks app access' }
];

const LegalDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeDoc, setActiveDoc] = useState('privacy_policy');
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [editedAckType, setEditedAckType] = useState('none');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Dialogs
  const [notifyDialog, setNotifyDialog] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [ackDialog, setAckDialog] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  
  // History & Acknowledgements
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [acknowledgements, setAcknowledgements] = useState(null);
  const [loadingAcks, setLoadingAcks] = useState(false);
  
  // Restore/Reack confirmations
  const [restoreDialog, setRestoreDialogOpen] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState(null);
  const [reackDialog, setReackDialog] = useState(false);
  const [requireReack, setRequireReack] = useState(false);

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/admin/documents');
      setDocuments(response.data.documents);
      setLabels(response.data.labels);
      
      const activeDocument = response.data.documents.find(d => d.type === activeDoc);
      if (activeDocument) {
        setEditedContent(activeDocument.content || '');
        setEditedTitle(activeDocument.title || '');
        setEditedAckType(activeDocument.acknowledgement_type || 'none');
      }
    } catch (error) {
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    const doc = documents.find(d => d.type === activeDoc);
    if (doc) {
      setEditedContent(doc.content || '');
      setEditedTitle(doc.title || '');
      setEditedAckType(doc.acknowledgement_type || 'none');
      setHasChanges(false);
    }
  }, [activeDoc, documents]);

  const handleContentChange = (value) => {
    setEditedContent(value);
    checkChanges(value, editedTitle, editedAckType);
  };

  const handleTitleChange = (value) => {
    setEditedTitle(value);
    checkChanges(editedContent, value, editedAckType);
  };

  const handleAckTypeChange = (value) => {
    setEditedAckType(value);
    checkChanges(editedContent, editedTitle, value);
  };

  const checkChanges = (content, title, ackType) => {
    const doc = documents.find(d => d.type === activeDoc);
    if (!doc) {
      setHasChanges(true);
      return;
    }
    const contentChanged = content !== (doc.content || '');
    const titleChanged = title !== (doc.title || '');
    const ackChanged = ackType !== (doc.acknowledgement_type || 'none');
    setHasChanges(contentChanged || titleChanged || ackChanged);
  };

  const handleSave = async (forceReack = false) => {
    setSaving(true);
    try {
      // Use FormData for proper encoding of rich text content
      const formData = new FormData();
      formData.append('title', editedTitle);
      formData.append('content', editedContent);
      formData.append('acknowledgement_type', editedAckType);
      formData.append('require_reacknowledgement', forceReack ? 'true' : 'false');
      
      const response = await api.put(`/admin/documents/${activeDoc}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.version_bumped && forceReack) {
        toast.success('Document saved - all users must re-acknowledge');
      } else {
        toast.success('Document saved');
      }
      
      setHasChanges(false);
      setReackDialog(false);
      setRequireReack(false);
      await fetchDocuments();
      
      // Only show notify dialog if there's actual content
      if (editedContent?.trim()) {
        setNotifyDialog(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleNotifyUsers = async (notifyAll) => {
    setSendingNotification(true);
    try {
      const response = await api.post(`/admin/documents/${activeDoc}/notify`, null, {
        params: { notify_all: notifyAll }
      });
      toast.success(`Notification sent to ${response.data.sent} users`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} notifications failed`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notifications');
    } finally {
      setSendingNotification(false);
      setNotifyDialog(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await api.get(`/admin/documents/${activeDoc}/history`);
      setHistory(response.data.history);
    } catch (error) {
      toast.error('Failed to fetch history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchAcknowledgements = async () => {
    setLoadingAcks(true);
    setAckDialog(true);
    try {
      const response = await api.get(`/admin/documents/${activeDoc}/acknowledgements`);
      setAcknowledgements(response.data);
    } catch (error) {
      toast.error('Failed to fetch acknowledgements');
    } finally {
      setLoadingAcks(false);
    }
  };

  const openRestoreDialog = (version) => {
    setVersionToRestore(version);
    setRestoreDialogOpen(true);
  };

  const handleRestore = async () => {
    if (!versionToRestore) return;
    
    try {
      await api.post(`/admin/documents/${activeDoc}/restore/${versionToRestore}`);
      toast.success(`Restored version ${versionToRestore}`);
      setHistoryDialog(false);
      setRestoreDialogOpen(false);
      setVersionToRestore(null);
      await fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore version');
    }
  };

  const currentDoc = documents.find(d => d.type === activeDoc);

  const getAckTypeBadge = (type) => {
    switch (type) {
      case 'hard':
        return <Badge className="bg-red-100 text-red-700">Signature Required</Badge>;
      case 'soft':
        return <Badge className="bg-amber-100 text-amber-700">Acknowledgement</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-600">No Ack Required</Badge>;
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Legal Documents">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Legal Documents">
      <div className="space-y-6" data-testid="legal-documents-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-slate-600">
              Manage legal documents with acknowledgement requirements
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreviewDialog(true)}
              data-testid="preview-doc-btn"
            >
              <Eye className="h-4 w-4 mr-1" />
              Preview
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                fetchHistory();
                setHistoryDialog(true);
              }}
              data-testid="history-btn"
            >
              <History className="h-4 w-4 mr-1" />
              History
            </Button>
            {currentDoc?.acknowledgement_type !== 'none' && (
              <Button
                variant="outline"
                size="sm"
                onClick={fetchAcknowledgements}
                data-testid="view-acks-btn"
              >
                <Users className="h-4 w-4 mr-1" />
                Acknowledgements
                {currentDoc?.acknowledgement_count > 0 && (
                  <Badge variant="secondary" className="ml-1">{currentDoc.acknowledgement_count}</Badge>
                )}
              </Button>
            )}
            <Button
              onClick={() => {
                // Check if acknowledgement type requires re-ack option
                const doc = documents.find(d => d.type === activeDoc);
                const hadAckBefore = doc?.acknowledgement_type !== 'none';
                const hasAckNow = editedAckType !== 'none';
                const contentChanged = editedContent !== (doc?.content || '');
                
                if ((hadAckBefore || hasAckNow) && contentChanged) {
                  setReackDialog(true);
                } else {
                  handleSave(false);
                }
              }}
              disabled={!hasChanges || saving}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="save-doc-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-1" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Document Tabs */}
        <Card className="border border-slate-200">
          <CardContent className="p-0">
            <Tabs value={activeDoc} onValueChange={setActiveDoc} className="w-full">
              <TabsList className="w-full justify-start rounded-none border-b bg-slate-50 p-0 h-auto flex-wrap">
                {Object.entries(labels).map(([type, label]) => {
                  const Icon = DOCUMENT_ICONS[type] || FileText;
                  const doc = documents.find(d => d.type === type);
                  return (
                    <TabsTrigger
                      key={type}
                      value={type}
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-500 data-[state=active]:bg-white px-4 py-3"
                      data-testid={`tab-${type}`}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">{label}</span>
                      <span className="sm:hidden">{label.split(' ')[0]}</span>
                      {doc?.version > 0 && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          v{doc.version}
                        </Badge>
                      )}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {Object.keys(labels).map((type) => (
                <TabsContent key={type} value={type} className="p-6 mt-0">
                  {/* Document Info */}
                  {currentDoc && (
                    <div className="mb-4 flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      {currentDoc.version > 0 ? (
                        <>
                          <span className="flex items-center gap-1">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Published (v{currentDoc.version})
                          </span>
                          {currentDoc.last_updated && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              Last updated: {new Date(currentDoc.last_updated).toLocaleDateString()}
                            </span>
                          )}
                          {currentDoc.updated_by_name && (
                            <span className="flex items-center gap-1">
                              <User className="h-4 w-4" />
                              By: {currentDoc.updated_by_name}
                            </span>
                          )}
                          {getAckTypeBadge(currentDoc.acknowledgement_type)}
                        </>
                      ) : (
                        <span className="flex items-center gap-1">
                          <AlertCircle className="h-4 w-4 text-amber-500" />
                          Not yet published
                        </span>
                      )}
                      {hasChanges && (
                        <Badge className="bg-amber-100 text-amber-700">Unsaved changes</Badge>
                      )}
                    </div>
                  )}

                  {/* Title Input */}
                  <div className="space-y-2 mb-4">
                    <Label>Document Title</Label>
                    <Input
                      value={editedTitle}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      placeholder="Enter document title..."
                      data-testid="doc-title-input"
                    />
                  </div>

                  {/* Acknowledgement Type */}
                  <div className="space-y-2 mb-4">
                    <Label>Acknowledgement Requirement</Label>
                    <Select value={editedAckType} onValueChange={handleAckTypeChange}>
                      <SelectTrigger className="w-full max-w-md" data-testid="ack-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ACKNOWLEDGEMENT_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      {ACKNOWLEDGEMENT_TYPES.find(t => t.value === editedAckType)?.description}
                    </p>
                  </div>

                  {editedAckType === 'hard' && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                        <div className="text-sm text-red-800">
                          <p className="font-medium">Hard Acknowledgement Warning</p>
                          <p className="mt-1">
                            Users will be blocked from accessing the app until they type their exact name and email 
                            to acknowledge this document. Use this for critical legal documents only.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Content Editor */}
                  <div className="space-y-2">
                    <Label>Document Content</Label>
                    <RichTextEditor
                      value={editedContent}
                      onChange={handleContentChange}
                      placeholder="Enter document content..."
                      minHeight="400px"
                      maxHeight="600px"
                      data-testid="doc-content-input"
                    />
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>

        {/* Re-acknowledge Dialog */}
        <AlertDialog open={reackDialog} onOpenChange={setReackDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-blue-600" />
                Require Re-Acknowledgement?
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>You've made changes to this document. Would you like to require users to re-acknowledge it?</p>
                  <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Checkbox
                      id="require-reack"
                      checked={requireReack}
                      onCheckedChange={setRequireReack}
                    />
                    <label htmlFor="require-reack" className="text-sm text-amber-800 cursor-pointer">
                      <strong>Require all users to re-acknowledge this document.</strong>
                      <br />
                      This will bump the version and require new acknowledgements from all users.
                    </label>
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleSave(requireReack)}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Notify Users Dialog */}
        <AlertDialog open={notifyDialog} onOpenChange={setNotifyDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-blue-600" />
                Notify Users?
              </AlertDialogTitle>
              <AlertDialogDescription>
                The document has been saved. Would you like to notify all users about this update?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={sendingNotification}>
                Later
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleNotifyUsers(true)}
                disabled={sendingNotification}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {sendingNotification ? 'Sending...' : 'Notify All Users'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* History Dialog */}
        <Dialog open={historyDialog} onOpenChange={setHistoryDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="h-5 w-5 text-slate-600" />
                Version History - {labels[activeDoc]}
              </DialogTitle>
            </DialogHeader>

            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <History className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                <p>No version history available</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((version) => (
                  <div
                    key={version.version}
                    className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">v{version.version}</Badge>
                        <span className="font-medium">{version.title}</span>
                        {version.acknowledgement_type && version.acknowledgement_type !== 'none' && (
                          getAckTypeBadge(version.acknowledgement_type)
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openRestoreDialog(version.version)}
                        data-testid={`restore-v${version.version}`}
                      >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Restore
                      </Button>
                    </div>
                    <div className="text-sm text-slate-500">
                      <span>{new Date(version.created_at).toLocaleString()}</span>
                    </div>
                    <div className="mt-2 text-sm text-slate-600 line-clamp-2">
                      {version.content?.replace(/<[^>]*>/g, '').substring(0, 150)}...
                    </div>
                  </div>
                ))}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setHistoryDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Restore Confirmation Dialog */}
        <AlertDialog open={restoreDialog} onOpenChange={setRestoreDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-blue-600" />
                Restore Version
              </AlertDialogTitle>
              <AlertDialogDescription>
                Restore version {versionToRestore}? This will create a new version with that content. The current content will be preserved in the history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleRestore}
                className="bg-blue-500 hover:bg-blue-600"
              >
                Restore
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Preview Dialog */}
        <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-slate-600" />
                Preview - {editedTitle || labels[activeDoc]}
              </DialogTitle>
              <DialogDescription>
                {getAckTypeBadge(editedAckType)}
              </DialogDescription>
            </DialogHeader>

            <div className="border border-slate-200 rounded-lg p-6 bg-white">
              <h1 className="text-2xl font-bold mb-4">{editedTitle || labels[activeDoc]}</h1>
              <div 
                className="prose prose-slate max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(editedContent ? markdownToHtml(editedContent) : '<p class="text-slate-400">No content yet...</p>') }}
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Acknowledgements Dialog */}
        <Dialog open={ackDialog} onOpenChange={setAckDialog}>
          <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Acknowledgements - {acknowledgements?.title || labels[activeDoc]}
              </DialogTitle>
              <DialogDescription>
                Version {acknowledgements?.version || 1} - {getAckTypeBadge(acknowledgements?.acknowledgement_type)}
              </DialogDescription>
            </DialogHeader>

            {loadingAcks ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : acknowledgements && (
              <div className="py-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-3xl font-bold text-slate-900">{acknowledgements.total_users}</p>
                      <p className="text-sm text-slate-500">Total Users</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-4 text-center">
                      <p className="text-3xl font-bold text-green-600">{acknowledgements.acknowledged_count}</p>
                      <p className="text-sm text-green-600">Acknowledged</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="pt-4 text-center">
                      <p className="text-3xl font-bold text-amber-600">{acknowledgements.pending_count}</p>
                      <p className="text-sm text-amber-600">Pending</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Acknowledgements List */}
                {acknowledgements.acknowledgements?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Acknowledged At</TableHead>
                        <TableHead>Typed Name</TableHead>
                        <TableHead>Typed Email</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {acknowledgements.acknowledgements.map((ack, index) => (
                        <TableRow key={ack.id || `ack-${index}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{ack.user_name}</p>
                              <p className="text-xs text-slate-500">{ack.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{ack.user_role}</Badge>
                          </TableCell>
                          <TableCell>
                            {new Date(ack.acknowledged_at).toLocaleString()}
                          </TableCell>
                          <TableCell className="font-mono text-sm">{ack.typed_name || '-'}</TableCell>
                          <TableCell className="font-mono text-sm">{ack.typed_email || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    <p>No acknowledgements yet for this version</p>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setAckDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default LegalDocuments;
